"use client";

import React from "react";
import { TriangleAlert as AlertTriangle, ArrowRight, Flame, PhoneCall, RefreshCw, ShieldAlert, Wrench, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ServiceItem {
  id: string;
  title: string;
  description: string;
  href: string;
  ctaText: string;
  badge: string;
  iconName: "wrench" | "refresh" | "zap" | "warning" | "emergency";
  isUrgent: boolean;
}
const servicesData: ServiceItem[] = [{
  id: "repair",
  title: "Gas Water Heater Repair",
  description: "Rapid diagnosis and repair for pilot lights, faulty thermocouples, gas control valves, and low hot water output in Jonesboro homes.",
  href: "/water-heater-repair/",
  ctaText: "Schedule Repair",
  badge: "Same-Day Fix",
  iconName: "wrench",
  isUrgent: false
}, {
  id: "replacement",
  title: "Gas Water Heater Replacement",
  description: "Full unit upgrades and professional installations when your tank is aging, leaking from the base, or beyond economical repair.",
  href: "/water-heater-replacement/",
  ctaText: "Explore Options",
  badge: "Energy Efficient",
  iconName: "refresh",
  isUrgent: false
}, {
  id: "tankless",
  title: "Tankless Water Heater Installation",
  description: "Endless on-demand hot water and reduced utility costs with high-efficiency modern tankless gas water heating systems.",
  href: "/tankless-water-heater-service/",
  ctaText: "View Tankless Solutions",
  badge: "Space Saver",
  iconName: "zap",
  isUrgent: false
}, {
  id: "problems",
  title: "Common Gas Water Heater Problems",
  description: "Troubleshooting guide and expert fixes for popping noises, rusty water, smelly water, temperature fluctuations, and slow reheating.",
  href: "/water-heater-problems/",
  ctaText: "Diagnose Issues",
  badge: "Troubleshooting",
  iconName: "warning",
  isUrgent: false
}, {
  id: "emergency-gas",
  title: "Emergency Gas Leak Response",
  description: "Immediate 24/7 dispatch for suspected gas leaks, sulfur odors, hissing lines, or sudden water heater system failures.",
  href: "/emergency-plumbing/",
  ctaText: "Get Emergency Help",
  badge: "24/7 Immediate Dispatch",
  iconName: "emergency",
  isUrgent: true
}];
function renderServiceIcon(iconName: ServiceItem["iconName"]) {
  switch (iconName) {
    case "wrench":
      return <Wrench className="size-6 text-primary" aria-hidden="true" />;
    case "refresh":
      return <RefreshCw className="size-6 text-primary" aria-hidden="true" />;
    case "zap":
      return <Zap className="size-6 text-accent-foreground" aria-hidden="true" />;
    case "warning":
      return <AlertTriangle className="size-6 text-chart-2" aria-hidden="true" />;
    case "emergency":
      return <Flame className="size-6 text-destructive" aria-hidden="true" />;
    default:
      return <Wrench className="size-6 text-primary" aria-hidden="true" />;
  }
}
export default function WaterHeaterServices() {
  const prefersReducedMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: prefersReducedMotion ? 0 : 0.08
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: prefersReducedMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: [0.25, 0.1, 0.25, 1]
      }
    }
  };
  return <section id="waterheaterservices" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <div className="flex flex-col items-center text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold uppercase tracking-wider text-xs">
            Jonesboro Emergency Plumber
          </Badge>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight">
            Gas &amp; Tankless Water Heater Services
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground">
            From same-day repairs and routine troubleshooting to energy-efficient
            replacements and 24/7 emergency response, our licensed technicians
            keep your hot water running safely.
          </p>
        </div>

        {/* Services Cards Grid */}
        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }}>
          {servicesData.map((service, i) => {
          const isEmergencyCard = service.isUrgent;
          return <motion.div key={service.id} data-index={i} variants={cardVariants} className={`flex ${isEmergencyCard ? "md:col-span-2 lg:col-span-2" : "col-span-1"}`}>
                <Card className={`w-full flex flex-col justify-between border shadow-sm transition-all duration-300 hover:shadow-md ${isEmergencyCard ? "bg-secondary/40 border-destructive/40 relative overflow-hidden" : "bg-card border-border hover:border-primary/40"}`}>
                  {isEmergencyCard && <div className="absolute top-0 right-0 w-32 h-32 -mr-10 -mt-10 bg-destructive/10 rounded-full blur-2xl pointer-events-none" />}

                  <div>
                    <CardHeader className="flex flex-row items-start justify-between gap-4 pb-4">
                      <div className={`p-3 rounded-lg flex items-center justify-center ${isEmergencyCard ? "bg-destructive/15 text-destructive" : "bg-muted"}`}>
                        {renderServiceIcon(service.iconName)}
                      </div>
                      <Badge variant={isEmergencyCard ? "destructive" : "outline"} className="font-medium text-xs">
                        {service.badge}
                      </Badge>
                    </CardHeader>

                    <CardContent className="space-y-3">
                      <CardTitle className="text-xl sm:text-2xl font-bold font-default text-card-foreground">
                        {service.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm leading-relaxed">
                        {service.description}
                      </CardDescription>
                    </CardContent>
                  </div>

                  <CardFooter className="pt-4 border-t border-border/60 flex items-center justify-between">
                    <Button asChild variant={isEmergencyCard ? "destructive" : "outline"} className="w-full sm:w-auto font-medium">
                      <Link to={service.href} className="inline-flex items-center justify-center gap-2">
                        <span>{service.ctaText}</span>
                        <ArrowRight className="size-4" aria-hidden="true" />
                      </Link>
                    </Button>

                    {isEmergencyCard && <div className="hidden sm:flex items-center gap-2 text-xs font-semibold text-destructive uppercase tracking-wide">
                        <ShieldAlert className="size-4" aria-hidden="true" />
                        <span>Immediate Response</span>
                      </div>}
                  </CardFooter>
                </Card>
              </motion.div>;
        })}
        </motion.div>

        {/* Emergency Callout Banner */}
        <div className="mt-12 rounded-xl bg-primary text-primary-foreground p-6 sm:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-md">
          <div className="flex items-center gap-4 text-center md:text-left">
            <div className="hidden sm:flex p-3 rounded-full bg-primary-foreground/10 text-primary-foreground shrink-0">
              <PhoneCall className="size-6" aria-hidden="true" />
            </div>
            <div>
              <h3 className="text-lg sm:text-xl font-bold font-default">
                Have an Urgent Water Heater Emergency in Jonesboro?
              </h3>
              <p className="text-sm sm:text-base text-primary-foreground/90 mt-1">
                Our local dispatch team is on call 24 hours a day, 7 days a week.
              </p>
            </div>
          </div>
          <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto shrink-0">
            <Button asChild variant="secondary" size="lg" className="w-full sm:w-auto font-semibold">
              <Link to="/emergency-plumbing/">
                <span>Call Emergency Service</span>
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="w-full sm:w-auto bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/20">
              <Link to="/contact/">
                <span>Contact Us</span>
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}